import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import AppIcon from '../../../../../components/AppIcon';
import { PreviewEndOfService } from '../../../../../api/Payroll/payroll';
import { DepartureReasonEnum, EosBreakdownView, PreviewEosInput } from '../../../../../models/payroll';
import { formatNumber } from '../../../../../utils';
import { ScenarioMatrix, ScenarioColumn, ScenarioRow } from '../../components/ScenarioMatrix';

type Dimension = 'reason' | 'contract' | 'date';

export interface EosScenarioBase {
  userId: string;
  endOfServiceId: number | null;
  departureReason: DepartureReasonEnum | '';
  contractType: string;
  departureDate: string;
  allowGratuityDespiteCause: boolean;
  art80ProcedureConfirmed: boolean;
  art80WarningOnFile: boolean;
}

interface ReasonOption {
  value: DepartureReasonEnum;
  labelKey: string;
}

interface Props {
  base: EosScenarioBase;
  currencyLabel: string;
  /** Combined generic + Art.44 reason list, used to label the reason columns. */
  reasonOptions: ReasonOption[];
}

interface Column {
  key: string;
  label: string;
  sublabel?: string;
  isCurrent: boolean;
  input: PreviewEosInput;
}

interface ResolvedColumn extends Column {
  data: EosBreakdownView | null;
}

// A short, meaningful set of reasons to line up against whatever the HR currently picked.
const REASON_SHORTLIST: DepartureReasonEnum[] = [
  'Resignation',
  'TerminationWithoutCause',
  'MutualAgreement',
  'Retirement',
];

const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addMonths = (iso: string, months: number): string => {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + months);
  return toIso(d);
};

const ScenarioComparison: React.FC<Props> = ({ base, currencyLabel, reasonOptions }) => {
  const { t } = useTranslation('common');
  const [dimension, setDimension] = useState<Dimension>('reason');
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [error, setError] = useState('');
  const [columns, setColumns] = useState<ResolvedColumn[]>([]);

  const complete = !!(base.departureReason && base.contractType && base.departureDate && base.endOfServiceId);

  const labelForReason = (r: DepartureReasonEnum): string => {
    const opt = reasonOptions.find(o => o.value === r);
    return t(opt ? opt.labelKey : r);
  };

  const baseInput = (over: Partial<Pick<PreviewEosInput, 'departureReason' | 'contractType' | 'departureDate'>>): PreviewEosInput => ({
    userId: base.userId,
    endOfServiceId: base.endOfServiceId as number,
    departureReason: (over.departureReason ?? base.departureReason) as DepartureReasonEnum,
    contractType: over.contractType ?? base.contractType,
    departureDate: over.departureDate ?? base.departureDate,
    allowGratuityDespiteCause: base.allowGratuityDespiteCause,
    art80ProcedureConfirmed: base.art80ProcedureConfirmed,
    art80WarningOnFile: base.art80WarningOnFile,
  });

  const buildColumns = (dim: Dimension): Column[] => {
    if (dim === 'contract') {
      return ['Limited', 'Unlimited'].map(ct => ({
        key: `ct-${ct}`,
        label: t(ct),
        isCurrent: ct === base.contractType,
        input: baseInput({ contractType: ct }),
      }));
    }
    if (dim === 'date') {
      return [
        { m: 0, label: t('As entered') },
        { m: 6, label: t('In 6 months') },
        { m: 12, label: t('In 1 year') },
      ].map(({ m, label }) => {
        const date = addMonths(base.departureDate, m);
        return {
          key: `d-${m}`,
          label,
          sublabel: appDate(date),
          isCurrent: m === 0,
          input: baseInput({ departureDate: date }),
        };
      });
    }
    // reason
    const seen = new Set<string>();
    const ordered = [base.departureReason as DepartureReasonEnum, ...REASON_SHORTLIST].filter(r => {
      if (seen.has(r)) return false;
      seen.add(r);
      return true;
    });
    return ordered.slice(0, 5).map(r => ({
      key: `r-${r}`,
      label: labelForReason(r),
      isCurrent: r === base.departureReason,
      input: baseInput({ departureReason: r }),
    }));
  };

  const run = async (dim: Dimension) => {
    if (!complete) return;
    setLoading(true);
    setError('');
    const cols = buildColumns(dim);
    const results = await Promise.all(
      cols.map(async c => {
        try {
          const r = await PreviewEndOfService(c.input);
          return r.errors?.length ? null : r.payload;
        } catch {
          return null;
        }
      }),
    );
    setColumns(cols.map((c, i) => ({ ...c, data: results[i] })));
    if (results.every(r => r === null)) setError(t('Comparison failed'));
    setHasRun(true);
    setLoading(false);
  };

  const selectDimension = (dim: Dimension) => {
    setDimension(dim);
    if (hasRun && complete) void run(dim);
  };

  const currentData = columns.find(c => c.isCurrent)?.data ?? null;

  const money = (v: number) => `${formatNumber(v)} ${currencyLabel}`.trim();

  const deltaChip = (col: ResolvedColumn): React.ReactNode => {
    if (col.isCurrent || !col.data || !currentData) return null;
    const d = col.data.finalGratuity - currentData.finalGratuity;
    if (Math.abs(d) < 0.005) return <span className="mt-0.5 block text-xs text-muted-foreground">{t('No change')}</span>;
    const up = d > 0;
    return (
      <span className={`mt-0.5 block text-xs font-medium ${up ? 'text-success' : 'text-destructive'}`}>
        {up ? '+' : '-'}
        {money(Math.abs(d))}
      </span>
    );
  };

  const matrixColumns: ScenarioColumn[] = columns.map(c => ({
    key: c.key,
    label: c.label,
    sublabel: c.sublabel,
    isCurrent: c.isCurrent,
  }));

  const cell = (col: ResolvedColumn, render: (d: EosBreakdownView) => React.ReactNode): React.ReactNode =>
    col.data ? render(col.data) : <span className="text-muted-foreground">—</span>;

  const rows: ScenarioRow[] = [
    {
      key: 'final',
      label: t('Final Gratuity'),
      emphasis: true,
      cells: columns.map(c => (
        <div key={c.key} className="flex flex-col items-end">
          <span className="text-primary">{cell(c, d => money(d.finalGratuity))}</span>
          {deltaChip(c)}
        </div>
      )),
    },
    {
      key: 'years',
      label: t('Service Years'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => d.serviceYearsDecimal.toFixed(2))}</span>),
    },
    {
      key: 'daily',
      label: t('Daily Rate'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => money(d.dailyRate))}</span>),
    },
    {
      key: 'raw',
      label: t('Gratuity before cap'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => money(d.rawGratuity))}</span>),
    },
    {
      key: 'factor',
      label: t('Law Factor'),
      cells: columns.map(c => <span key={c.key}>{cell(c, d => `${d.lawFactor}×`)}</span>),
    },
    {
      key: 'cap',
      label: t('Two-year cap'),
      cells: columns.map(c => (
        <span key={c.key}>
          {cell(c, d =>
            d.isCapped ? (
              <span className="inline-flex items-center rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning">
                {t('Capped')}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            ),
          )}
        </span>
      )),
    },
  ];

  const dimensions: { id: Dimension; label: string }[] = [
    { id: 'reason', label: t('Departure Reason') },
    { id: 'contract', label: t('Contract Type') },
    { id: 'date', label: t('Departure Date') },
  ];

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <AppIcon name="Scale" size={20} className="mt-0.5 shrink-0 text-primary" />
          <div className="flex flex-col gap-0.5">
            <h4 className="text-sm font-semibold text-foreground">{t('What-if comparison')}</h4>
            <p className="max-w-[52ch] text-xs text-muted-foreground">
              {t('Compare the gratuity outcome across scenarios. Nothing is saved.')}
            </p>
          </div>
        </div>
        <div className="inline-flex flex-wrap gap-1 rounded-lg border border-border bg-muted/40 p-1" role="group" aria-label={t('Compare by')}>
          {dimensions.map(dim => (
            <button
              key={dim.id}
              type="button"
              onClick={() => selectDimension(dim.id)}
              disabled={!complete || loading}
              aria-pressed={dimension === dim.id}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                dimension === dim.id ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {dim.label}
            </button>
          ))}
        </div>
      </div>

      {!complete ? (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          <AppIcon name="Info" size={16} className="shrink-0" />
          <p>{t('Fill in the departure reason, contract type, date and EOS type above to compare scenarios.')}</p>
        </div>
      ) : !hasRun ? (
        <div>
          <button
            type="button"
            onClick={() => run(dimension)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && <AppIcon name="LoaderCircle" size={16} className="animate-spin" />}
            {t('Compare scenarios')}
          </button>
        </div>
      ) : (
        <>
          {error ? (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <AppIcon name="AlertTriangle" size={16} className="shrink-0" />
              <p>{error}</p>
            </div>
          ) : (
            <ScenarioMatrix columns={matrixColumns} rows={rows} currentLabel={t('Current')} metricHeader={t('Scenario')} />
          )}
          <div>
            <button
              type="button"
              onClick={() => run(dimension)}
              disabled={loading}
              className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              {loading ? <AppIcon name="LoaderCircle" size={14} className="animate-spin" /> : <AppIcon name="RefreshCw" size={14} />}
              {t('Refresh comparison')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ScenarioComparison;
